import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cx } from '../components/ui';
import { clock } from '../lib/format';
import type { BubbleCorner } from './composer';
import { BUBBLE_CORNERS } from './composer';
import type { RecorderApi } from './useRecorder';

/**
 * What the person recording sees: the composed frame, the clock, and the
 * webcam bubble they can drag into whichever corner is not covering the thing
 * they are talking about.
 *
 * The bubble here is a *proxy* for the one being burned into the canvas — drag
 * it, and on release it snaps to a corner and tells the compositor. Dragging
 * a live video element around at 30fps to preview something the encoder is
 * already doing would be all cost and no information.
 */

function useStreamElement(stream: MediaStream | null) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (element.srcObject !== stream) element.srcObject = stream;
    if (stream) {
      void element.play().catch(() => {
        // A monitor that will not autoplay is cosmetic; recording continues.
      });
    }
  }, [stream]);

  return ref;
}

const CORNER_CLASS: Record<BubbleCorner, string> = {
  'top-left': 'left-[3.5%] top-[3.5%]',
  'top-right': 'right-[3.5%] top-[3.5%]',
  'bottom-left': 'bottom-[3.5%] left-[3.5%]',
  'bottom-right': 'bottom-[3.5%] right-[3.5%]',
};

function nearestCorner(xRatio: number, yRatio: number): BubbleCorner {
  const vertical = yRatio < 0.5 ? 'top' : 'bottom';
  const horizontal = xRatio < 0.5 ? 'left' : 'right';
  return `${vertical}-${horizontal}` as BubbleCorner;
}

function CameraBubble({ recorder }: { recorder: RecorderApi }) {
  const videoRef = useStreamElement(recorder.cameraMonitor);
  const containerRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ x: number; y: number } | null>(null);

  if (!recorder.hasCamera || !recorder.bubble.visible) return null;

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({ x: event.clientX, y: event.clientY });
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    setDrag({ x: event.clientX, y: event.clientY });
  };

  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    const parent = containerRef.current?.parentElement;
    if (parent) {
      const rect = parent.getBoundingClientRect();
      recorder.moveBubble(
        nearestCorner(
          (event.clientX - rect.left) / Math.max(rect.width, 1),
          (event.clientY - rect.top) / Math.max(rect.height, 1),
        ),
      );
    }
    setDrag(null);
  };

  const dragStyle = drag
    ? ({
        position: 'fixed' as const,
        left: drag.x,
        top: drag.y,
        transform: 'translate(-50%, -50%)',
        right: 'auto',
        bottom: 'auto',
      })
    : undefined;

  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={dragStyle}
      className={cx(
        'group/bubble absolute z-10 aspect-square w-[22%] max-w-[160px] cursor-grab touch-none overflow-hidden rounded-full border-2 border-chalk/30 bg-slate-950 shadow-raised',
        drag ? 'cursor-grabbing opacity-90' : CORNER_CLASS[recorder.bubble.corner],
      )}
      title="Drag me into a corner"
    >
      <video
        ref={videoRef}
        muted
        playsInline
        className={cx('h-full w-full object-cover', recorder.bubble.mirror && 'scale-x-[-1]')}
      />
    </div>
  );
}

function Countdown({ value, onSkip }: { value: number; onSkip: () => void }) {
  return (
    <button
      type="button"
      onClick={onSkip}
      className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-slate-950/80 backdrop-blur-sm"
    >
      <span
        key={value}
        className="slate-type animate-fade-up text-display font-semibold text-chalk"
        data-timecode
      >
        {value}
      </span>
      <span className="mt-2 text-caption text-chalk-faint">Click to start now</span>
    </button>
  );
}

export function RecorderStage({ recorder }: { recorder: RecorderApi }) {
  const videoRef = useStreamElement(recorder.monitor);
  const recording = recorder.phase === 'recording';
  const paused = recorder.phase === 'paused';

  return (
    <div className="relative overflow-hidden rounded-md border border-hairline bg-slate-900">
      <div className="clapper-stripes absolute inset-x-0 top-0 z-10 h-1.5 opacity-[0.14]" aria-hidden="true" />

      <div className="relative aspect-video w-full">
        {recorder.monitor ? (
          <video
            ref={videoRef}
            muted
            playsInline
            className="h-full w-full bg-black object-contain"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-center">
            <p className="text-body text-chalk-dim">Nothing selected yet</p>
            <p className="max-w-xs text-caption text-chalk-faint">
              Pick a screen, a window, or a browser tab and it will show up here before you
              start recording.
            </p>
          </div>
        )}

        <CameraBubble recorder={recorder} />

        {recorder.phase === 'countdown' ? (
          <Countdown value={recorder.countdown} onSkip={recorder.skipCountdown} />
        ) : null}

        {recording || paused ? (
          <div className="absolute left-3 top-4 z-10 flex items-center gap-2 rounded bg-slate-950/80 px-2.5 py-1.5">
            <span
              className={cx(
                'h-2 w-2 rounded-full',
                recording ? 'animate-record-pulse bg-record' : 'bg-chalk-faint',
              )}
              aria-hidden="true"
            />
            <span className="slate-type text-caption text-chalk" data-timecode>
              {clock(recorder.elapsedMs / 1000)}
            </span>
            {paused ? <span className="text-micro uppercase text-chalk-faint">Paused</span> : null}
          </div>
        ) : null}
      </div>

      {recorder.hasCamera && recorder.phase !== 'recording' && recorder.phase !== 'paused' ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-hairline px-3 py-2">
          <span className="text-caption text-chalk-faint">Bubble</span>
          {BUBBLE_CORNERS.map((corner) => (
            <button
              key={corner}
              type="button"
              onClick={() => recorder.moveBubble(corner)}
              aria-pressed={recorder.bubble.corner === corner}
              className={cx(
                'h-6 w-6 rounded-xs border transition-colors duration-120',
                recorder.bubble.corner === corner
                  ? 'border-chalk/40 bg-slate-700'
                  : 'border-hairline bg-slate-900 hover:border-slate-600',
              )}
              title={corner.replace('-', ' ')}
            >
              <span
                aria-hidden="true"
                className={cx(
                  'block h-1.5 w-1.5 rounded-full bg-chalk-dim',
                  corner === 'top-left' && 'ml-1 mt-1',
                  corner === 'top-right' && 'ml-auto mr-1 mt-1',
                  corner === 'bottom-left' && 'ml-1 mt-auto mb-1',
                  corner === 'bottom-right' && 'ml-auto mr-1 mt-auto mb-1',
                )}
              />
            </button>
          ))}
          <button
            type="button"
            onClick={() => recorder.setBubble({ mirror: !recorder.bubble.mirror })}
            className="ml-2 text-caption text-chalk-faint underline-offset-2 hover:text-chalk-dim hover:underline"
          >
            {recorder.bubble.mirror ? 'Mirrored' : 'Not mirrored'}
          </button>
          <button
            type="button"
            onClick={() => recorder.setBubble({ visible: !recorder.bubble.visible })}
            className="text-caption text-chalk-faint underline-offset-2 hover:text-chalk-dim hover:underline"
          >
            {recorder.bubble.visible ? 'Hide bubble' : 'Show bubble'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
